import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hammer, Phone, Menu, X, ArrowRight } from "lucide-react";
import { COMPANY, scrollToId } from "@/lib/site";

const LINKS = [
    { id: "home", label: "Home", testid: "nav-link-home" },
    { id: "about", label: "About", testid: "nav-link-about" },
    { id: "services", label: "Services", testid: "nav-link-services" },
    { id: "projects", label: "Projects", testid: "nav-link-projects" },
    { id: "why-us", label: "Why Us", testid: "nav-link-why-us" },
    { id: "faqs", label: "FAQs", testid: "nav-link-faqs" },
    { id: "contact", label: "Contact", testid: "nav-link-contact" },
];

export default function StickyNavbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 150 : 0);
    };

    return (
        <header
            data-testid="nav-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
                scrolled ? "bg-white/90 backdrop-blur-md border-[#E5E5E0] shadow-[0_4px_24px_rgba(0,0,0,0.05)]" : "bg-transparent border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-[72px]">
                    <button
                        data-testid="nav-logo-link"
                        onClick={() => go("home")}
                        className="flex items-center gap-3 group"
                        aria-label="Appex Smart Solution home"
                    >
                        <span className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300 ${scrolled ? "bg-[#E85A1D]" : "bg-[#E85A1D]"}`}>
                            <Hammer className="w-5 h-5 text-white" strokeWidth={2.2} />
                        </span>
                        <span className="text-left leading-none">
                            <span className={`font-display font-extrabold tracking-tight text-lg block transition-colors duration-300 ${scrolled ? "text-neutral-900" : "text-white"}`}>
                                APPEX
                            </span>
                            <span className={`font-mono-brand text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${scrolled ? "text-neutral-500" : "text-white/60"}`}>
                                Smart Solution
                            </span>
                        </span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-7" aria-label="Main navigation">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={l.testid}
                                onClick={() => go(l.id)}
                                className={`text-sm font-medium transition-colors duration-200 hover:text-[#E85A1D] ${
                                    scrolled ? "text-neutral-700" : "text-white/85"
                                }`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-4">
                        <a
                            data-testid="nav-phone-link"
                            href={COMPANY.phoneHref}
                            className={`flex items-center gap-2 text-sm font-semibold transition-colors duration-200 hover:text-[#E85A1D] ${
                                scrolled ? "text-neutral-800" : "text-white"
                            }`}
                        >
                            <Phone className="w-4 h-4" />
                            {COMPANY.phoneDisplay}
                        </a>
                        <button
                            data-testid="nav-cta-quote-button"
                            onClick={() => go("contact")}
                            className="group flex items-center gap-2 bg-[#E85A1D] hover:bg-[#D24B10] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-200"
                        >
                            Get a Quote
                            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </button>
                    </div>

                    <button
                        data-testid="nav-mobile-hamburger"
                        onClick={() => setOpen(!open)}
                        className={`lg:hidden w-11 h-11 flex items-center justify-center rounded-xl transition-colors duration-200 ${scrolled || open ? "text-neutral-900" : "text-white"}`}
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-white border-b border-[#E5E5E0]"
                    >
                        <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile navigation">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.3 }}
                                    onClick={() => go(l.id)}
                                    className="text-left font-display font-bold text-2xl text-neutral-900 py-2.5 hover:text-[#E85A1D] transition-colors duration-200"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <div className="flex flex-col gap-3 pt-5 mt-3 border-t border-[#E5E5E0]">
                                <a
                                    href={COMPANY.phoneHref}
                                    className="flex items-center justify-center gap-2 border-2 border-neutral-900 text-neutral-900 font-bold px-5 py-3.5 rounded-full"
                                >
                                    <Phone className="w-4 h-4" />
                                    Call {COMPANY.phoneDisplay}
                                </a>
                                <button
                                    onClick={() => go("contact")}
                                    className="bg-[#E85A1D] text-white font-bold px-5 py-3.5 rounded-full"
                                >
                                    Get a Free Quote
                                </button>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
